import { useCallback, useEffect, useRef, useState } from 'react';
import { InteractionManager } from '@/SDK/InteractionManager';
import runtimeConfig from '@/utils/runtimeConfig';

const POLL_INTERVAL_MS = 30_000;

export function useBackendHealthMonitor(): {
	isDown: boolean;
	isChecking: boolean;
	checkNow: () => Promise<void>;
} {
	const [isDown, setIsDown] = useState(false);
	const [isChecking, setIsChecking] = useState(false);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const stopPolling = useCallback(() => {
		if (intervalRef.current !== null) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
	}, []);

	const startPolling = useCallback((checkFn: () => Promise<void>) => {
		if (intervalRef.current === null) {
			intervalRef.current = setInterval(checkFn, POLL_INTERVAL_MS);
		}
	}, []);

	const check = useCallback(async (): Promise<void> => {
		setIsChecking(true);
		try {
			const res = runtimeConfig.isFedEx
				? await InteractionManager.getInstance().API.getFedExAlive()
				: await InteractionManager.getInstance().API.getAlive();
			if (res.Error) {
				setIsDown(true);
			} else {
				setIsDown(false);
				stopPolling();
			}
		} catch {
			setIsDown(true);
		} finally {
			setIsChecking(false);
		}
	}, [stopPolling]);

	useEffect(() => {
		check();

		const handleEvent = () => {
			setIsDown(true);
			startPolling(check);
		};

		window.addEventListener('backend-unavailable', handleEvent);

		// Start polling initially to catch outages
		startPolling(check);

		return () => {
			stopPolling();
			window.removeEventListener('backend-unavailable', handleEvent);
		};
	}, [check, startPolling, stopPolling]);

	return { isDown, isChecking, checkNow: check };
}
